import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';

import definite from 'webapp/helpers/definite';
import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import safeStyle from 'webapp/helpers/safe-style';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonRecipientsListComponent from 'webapp/components/common/recipients-list';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DummyCellComponent from 'webapp/components/dummy-cell';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import OptionsButtonComponent from 'webapp/components/options-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Notification } from 'webapp/models-custom';
import type BatchSelect from 'webapp/models-custom/batch-select';
import Goal from 'webapp/models-custom/goal';

export interface Args {
	model: Notification;
	batchSelect: BatchSelect<Notification>;
}

type Action = 'edit' | 'delete' | 'copy' | 'preview' | 'openActivityLog' | 'handleLimitation';
type Option = PopupListItem<Action>;

export default class NotificationsTableRowComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return batchSelect.isItemSelected(model);
	}

	@cached
	get deliveryTypesIcons() {
		return this.args.model.deliveryTypesIcons;
	}

	get showToggle() {
		return this.args.model.canToggle && this.billingService.sharingEnabled;
	}

	get options() {
		const options: Option[] = [];

		const model = this.args.model;
		const isLocked = model.isLocked;

		const hasBillingLimitation = this.billingService.sharingEnabled;

		const canEdit = hasBillingLimitation ? model.canEdit : false;
		const canDelete = hasBillingLimitation ? model.canEdit : true;

		if (canEdit) {
			options.push({
				icon: 'Pencil',
				text: 'Edit',
				action: isLocked ? 'handleLimitation' : 'edit',
				extra: isLocked ? { type: 'featureLimitation' } : undefined,
			});

			if (model.canPreview) {
				options.push({
					icon: 'EyeOpen',
					text: 'Preview',
					action: 'preview',
				});
			}

			if (model.canDuplicate) {
				options.push({
					icon: 'Copy',
					text: 'Duplicate',
					action: isLocked ? 'handleLimitation' : 'copy',
					extra: isLocked ? { type: 'featureLimitation' } : undefined,
				});
			}
		}

		// options.push(this.mmActivityLogsService.getObjectForOptionsPopup<Action>());

		if (canDelete) {
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		return options;
	}

	@action
	doOptionsAction(actionName: string): void {
		const fn = this[actionName as Action];
		fn?.();
	}

	@action
	edit() {
		if (this.accountService.handleDemo()) {
			return;
		}
		const targetObj = this.args.model.targetObject;
		if (targetObj instanceof Goal) {
			this.mmGoalsService.openEditPopup(targetObj, 'notifications', undefined, true);
		} else {
			this.mmNotificationsService.openEditPopupWithTransition(targetObj);
		}
	}

	@action
	delete() {
		this.args.batchSelect.unselectAll();
		this.mmNotificationsService.confirmAndDeleteNotification(this.args.model).catch(() => {
			// user rejected, all ok
		});
	}

	@action
	copy() {
		if (!this.accountService.handleDemo()) {
			this.args.model.duplicate();
		}
	}

	@action
	openActivityLog() {
		// TODO
	}

	@action
	handleLimitation() {
		this.args.model.handleLimitation();
	}

	@action
	toggle() {
		this.args.model.toggle();
	}

	@action
	preview() {
		this.args.model.preview();
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		{{#if this.isVisibleOnScreen}}

			<tr
				class={{joinClasses
					(array
						"table-row" (if this.showAll "all-shown") (if this.isSelected "selected")
					)
				}}
				data-id={{@model.id}}
			>
				<td class="cc first">
					<FormCheckboxComponent
						@name="notifications-table-row"
						@selected={{this.isSelected}}
						@theme="blue"
						@onChange={{fn @batchSelect.changeSelection @model}}
					/>
				</td>

				<td class="c0 no-grow">
					<div
						class="notification-type-icon"
						style={{safeStyle @model.typeBgColor property="background-color"}}
					>
						<CommonUiIconComponent
							@name={{definite @model.typeIcon}}
							@fill={{@model.typeColor}}
							@size="md"
						/>
						<ToolTipComponent @text={{@model.typeText}} @position="top" />
					</div>
				</td>

				<td class="c1">
					<div class="flex">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{false}}
							@editableProperty="title"
						/>
					</div>
					<small>
						{{#if @model.subtitleImage}}
							<img width="16" height="16" src={{@model.subtitleImage}} alt />
						{{/if}}
						{{highlightResult text=@model.subtitleText match=@model.activeFilter}}
					</small>
				</td>

				<td class="c2 frequency prio-medium" data-label="Frequency: ">
					<strong>
						{{@model.frequencyText}}
						<ToolTipComponent
							@text={{@model.frequencyTooltipText}}
							@position="top"
							@maxWidth={{230}}
						/>
					</strong>
					<br />
					<span>{{@model.scheduleTypeText}}</span>
				</td>

				<td class="c3 prio-low" data-label="Delivery options: ">
					{{#each this.deliveryTypesIcons as |iconData|}}
						<span>
							{{#if iconData.isStrokeIcon}}
								<CommonUiIconComponent
									@name={{iconData.icon}}
									@size="sm"
									@stroke="currentColor"
									@fill="none"
								/>
							{{else}}
								<CommonUiIconComponent @name={{iconData.icon}} @size="sm" />
							{{/if}}
							<ToolTipComponent @text={{iconData.text}} @position="top" />
						</span>
					{{/each}}
				</td>

				<td class="c4 prio-low" data-label="Recipient(s): ">
					<CommonRecipientsListComponent
						@recipients={{@model.allRecipients}}
						@activeFilter={{@model.activeFilter}}
					/>
				</td>

				<td class="c5 prio-medium" data-label="Active for me: ">
					{{#if this.showToggle}}
						<span>
							{{#if @model.isToggleOn}}
								<ToolTipComponent
									@text="Disabling will turn off delivery for you and will not affect other recipients."
									@maxWidth={{230}}
								/>
							{{/if}}

							<FormCheckboxSwitchComponent
								@name="isActive"
								@selected={{@model.isToggleOn}}
								@onChange={{this.toggle}}
								@isDisabled={{this.accountService.isDemoAccount}}
							/>
						</span>
					{{/if}}
				</td>

				<td class="button-ct last">
					<OptionsButtonComponent
						@options={{this.options}}
						@onAction={{this.doOptionsAction}}
					/>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>

		{{else}}

			<tr class="table-row" {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
